import { useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c, msgid } from 'ttag';

import { Button, FilterPill, Input, Pagination, RegionState } from '@freelancelabs/components';
import { MagnifyingGlass } from '@freelancelabs/icons';

import ListCard from '../../components/ListCard';
import { AccessibleAccountsDocument, ActivityLogDocument } from '../../graphql/__generated__/types';
import JournalTable from './JournalTable';
import { CURATED_ACTION_CODES, actionLabel } from './actionCatalog';
import { journalRefusalOf } from './journalRefusal';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 300;

// Lower bound only; ALL constrains nothing and travels as null.
type Period = 'ALL' | 'P7D' | 'P30D' | 'P3M' | 'P12M';

const periodFrom = (period: Period): string | null => {
    if (period === 'ALL') {
        return null;
    }
    const date = new Date();
    switch (period) {
        case 'P7D':
            date.setDate(date.getDate() - 7);
            break;
        case 'P30D':
            date.setDate(date.getDate() - 30);
            break;
        case 'P3M':
            date.setMonth(date.getMonth() - 3);
            break;
        case 'P12M':
            date.setFullYear(date.getFullYear() - 1);
            break;
    }
    return date.toISOString();
};

const JournalPage = () => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [selectedActions, setSelectedActions] = useState<string[]>([]);
    const [organizationId, setOrganizationId] = useState<string>('');
    const [period, setPeriod] = useState<Period>('ALL');
    const [page, setPage] = useState(1);

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    // Recomputed only on preset change: recomputed every render, its timestamp would loop the query.
    const from = useMemo(() => periodFrom(period), [period]);

    const actionsKey = selectedActions.join(',');
    // Any criterion change returns to page 1: staying on page 3 of a shorter result shows an empty table.
    useEffect(() => setPage(1), [debouncedSearch, actionsKey, organizationId, period]);

    const { data: accountsData } = useQuery(AccessibleAccountsDocument);
    const accounts = accountsData?.accessibleAccounts ?? [];

    const { data, loading, error } = useQuery(ActivityLogDocument, {
        variables: {
            // No type checked: send the curated list, never an empty array (which means "all" to the API).
            actions: selectedActions.length > 0 ? selectedActions : CURATED_ACTION_CODES,
            organizationId: organizationId || null,
            from,
            to: null,
            search: debouncedSearch || null,
            limit: PAGE_SIZE,
            offset: (page - 1) * PAGE_SIZE,
        },
        fetchPolicy: 'cache-and-network',
    });

    const result = data?.activityLog;
    const items = result?.items ?? [];
    // Never items.length: that would count a page. The API total follows the filters.
    const total = result?.total ?? null;

    const filtered =
        Boolean(debouncedSearch) || selectedActions.length > 0 || organizationId !== '' || period !== 'ALL';
    const showSkeleton = loading && !data;
    const refusal = error ? journalRefusalOf(error) : null;

    const rangeStart = (page - 1) * PAGE_SIZE + 1;
    const rangeEnd = (page - 1) * PAGE_SIZE + items.length;
    const summary =
        total === null
            ? c('Activity log').t`${rangeStart}–${rangeEnd}`
            : c('Activity log').ngettext(
                  msgid`${rangeStart}–${rangeEnd} of ${total} event`,
                  `${rangeStart}–${rangeEnd} of ${total} events`,
                  total
              );

    const actionOptions = useMemo(
        () =>
            CURATED_ACTION_CODES.map((code) => ({
                value: code,
                label: actionLabel(code),
                // Searchable by label and by raw code.
                searchText: `${actionLabel(code)} ${code}`,
            })),
        []
    );

    const accountOptions = useMemo(
        () => [
            { value: '', label: c('Activity log filter').t`All accounts` },
            // An account with no id cannot filter; a missing name falls back to the id, not a blank label.
            ...accounts
                .filter((account): account is typeof account & { id: string } => Boolean(account.id))
                .map((account) => ({ value: account.id, label: account.name ?? account.id })),
        ],
        [accounts]
    );

    const periodOptions: { value: Period; label: string }[] = [
        { value: 'ALL', label: c('Activity log filter').t`Since the beginning` },
        { value: 'P7D', label: c('Activity log filter').t`Last 7 days` },
        { value: 'P30D', label: c('Activity log filter').t`Last 30 days` },
        { value: 'P3M', label: c('Activity log filter').t`Last 3 months` },
        { value: 'P12M', label: c('Activity log filter').t`Last 12 months` },
    ];

    const clearFilters = () => {
        setSearch('');
        setDebouncedSearch('');
        setSelectedActions([]);
        setOrganizationId('');
        setPeriod('ALL');
    };

    const state = refusal ? (
        <RegionState
            tone="error"
            title={refusal.title}
            description={
                <>
                    {refusal.guidance}
                    {refusal.detail && (
                        <span className="mt-2 block break-words rounded-md border border-border-norm bg-background-weak p-2 font-mono text-xs">
                            {refusal.detail}
                        </span>
                    )}
                </>
            }
        />
    ) : !showSkeleton && items.length === 0 && filtered ? (
        <RegionState
            title={c('Activity log').t`No event matches these criteria.`}
            description={c('Activity log')
                .t`The search reads the account name and the actor's name or address, and the other filters narrow it further. Widen the period, or clear a filter, to see more.`}
        >
            <Button category="weak" size="small" onClick={clearFilters}>
                {c('Activity log').t`Clear filters`}
            </Button>
        </RegionState>
    ) : !showSkeleton && items.length === 0 && !filtered ? (
        <RegionState
            title={c('Activity log').t`No administration gesture has been logged yet.`}
            description={c('Activity log')
                .t`The audit log fills as accounts are configured, users invited and roles changed. Token issuance and other operational traffic are deliberately left out of it.`}
        />
    ) : null;

    return (
        <section className="flex flex-col gap-5">
            <header className="flex flex-wrap items-start gap-4">
                <div className="min-w-0">
                    <h1 className="text-xl font-semibold text-text-900">{c('Activity log').t`Activity log`}</h1>
                </div>
                <span className="ml-auto inline-flex shrink-0 items-center gap-2 rounded-full border border-border-norm bg-background-norm px-3 py-1.5 text-[11.5px] font-semibold text-text-500">
                    <span className="h-1.5 w-1.5 rounded-full bg-[color:var(--badge-user-color)]" aria-hidden="true" />
                    {c('Activity log').t`Audit · read-only`}
                </span>
            </header>

            <ListCard
                label={c('Activity log').t`Activity log`}
                toolbar={
                    <div className="flex flex-wrap items-center gap-3">
                        <Input
                            value={search}
                            onValue={setSearch}
                            clearable
                            prefix={<MagnifyingGlass size={16} />}
                            placeholder={c('Activity log filter').t`Search an actor, an object, an account…`}
                            aria-label={c('Activity log filter').t`Search the log`}
                            className="max-w-sm"
                        />

                        <FilterPill
                            label={c('Activity log filter').t`Action type`}
                            options={actionOptions}
                            values={selectedActions}
                            onValues={setSelectedActions}
                            count={selectedActions.length || undefined}
                            searchLabel={c('Activity log filter').t`Search an action`}
                            noMatchLabel={c('Activity log filter').t`No action matches this search.`}
                        />

                        <FilterPill
                            label={c('Activity log filter').t`Account`}
                            mode="single"
                            options={accountOptions}
                            values={[organizationId]}
                            onValues={([value]) => setOrganizationId(value)}
                            // "All accounts" subtracts nothing: no badge on that choice.
                            count={organizationId ? 1 : undefined}
                            searchLabel={c('Activity log filter').t`Search an account`}
                            emptyLabel={c('Activity log filter').t`No account to filter on.`}
                            noMatchLabel={c('Activity log filter').t`No account matches this search.`}
                        />

                        <FilterPill
                            label={c('Activity log filter').t`Period`}
                            mode="single"
                            options={periodOptions}
                            values={[period]}
                            onValues={([value]) => setPeriod(value)}
                            count={period === 'ALL' ? undefined : 1}
                        />

                        {filtered && (
                            <Button className="ml-auto" category="link" size="small" onClick={clearFilters}>
                                {c('Activity log filter').t`Reset`}
                            </Button>
                        )}
                    </div>
                }
                footer={
                    !showSkeleton &&
                    !error &&
                    items.length > 0 && (
                        <div className="px-4">
                            <Pagination
                                page={page}
                                pageSize={PAGE_SIZE}
                                totalCount={total}
                                summary={summary}
                                onPageChange={setPage}
                            />
                        </div>
                    )
                }
            >
                <JournalTable rows={items} loading={showSkeleton} hasError={Boolean(error)} state={state} />
            </ListCard>
        </section>
    );
};

export default JournalPage;
